import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_ROUTES, API_DOMAIN, headers } from '../../../Reusable_Components/Config/apiConfig';
import { ToastContext } from '../../../../context/ToastContext';
import { fmtDate } from '../../../../utils/fmt';
import ConfirmModal from '../../../Reusable_Components/ConfirmModal';
import ConvertActionButton from '../../../Reusable_Components/ConvertActionButton';
import useDetailFetch from '../../../../hooks/useDetailFetch';

const STATUS_COLORS = {
  'Prospect': '#f59e0b', 'Active': '#22c55e', 'Inactive': '#94a3b8',
  'At Risk': '#ef4444', 'Former': '#64748b', 'VIP': '#8b5cf6',
};

const TYPE_ICONS = {
  'Individual': '👤', 'Business': '🏢', 'Enterprise': '🏛', 'Partner': '🤝',
};

const TIER_CONFIG = {
  Platinum: { bg: '#f5f3ff', fg: '#6d28d9', bar: '#8b5cf6' },
  Gold:     { bg: '#fffbeb', fg: '#92400e', bar: '#f59e0b' },
  Silver:   { bg: '#f8fafc', fg: '#475569', bar: '#94a3b8' },
  Bronze:   { bg: '#fff7ed', fg: '#9a3412', bar: '#f97316' },
  Standard: { bg: '#f0fdf4', fg: '#166534', bar: '#22c55e' },
};


const fmtKES = (n) => {
  const num = Number(n);
  if (!num || isNaN(num)) return '—';
  return 'KES ' + num.toLocaleString('en-KE');
};

const Section = ({ icon, title, children }) => (
  <div className="card" style={{ marginBottom: 0 }}>
    <div className="card-body">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
        <div style={{ width: 32, height: 32, borderRadius: 7, background: 'var(--color-primary-pale)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{icon}</div>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>{title}</span>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, value, wide, href }) => (
  <div className={`form-field${wide ? ' span-full' : ''}`}>
    <div className="form-label">{label}</div>
    <div style={{ fontSize: 14, color: value ? 'var(--text-main)' : 'var(--text-muted)', fontWeight: value ? 500 : 400, padding: '8px 12px', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)', minHeight: 38, wordBreak: 'break-word' }}>
      {href && value
        ? <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>{value}</a>
        : (value || '—')
      }
    </div>
  </div>
);

const SingleCustomerDetails = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  const id = customerId ? atob(customerId).replace(/\D/g, '') : '';
  const { data: customer, loading } = useDetailFetch(id ? `${API_DOMAIN}${API_ROUTES.customer}${id}/` : null);
  const [showDelete, setShowDelete] = useState(false);
  const [sourceOpp, setSourceOpp] = useState(null);
  const [sourceLead, setSourceLead] = useState(null);
  const [expansionOpps, setExpansionOpps] = useState([]);

  useEffect(() => {
    if (!customer?.source_opportunity) return;
    axios.get(`${API_DOMAIN}${API_ROUTES.opportunity}${customer.source_opportunity}/`, { headers })
      .then(res => {
        setSourceOpp(res.data);
        if (res.data.related_lead) {
          axios.get(`${API_DOMAIN}${API_ROUTES.lead}${res.data.related_lead}/`, { headers })
            .then(r => setSourceLead(r.data)).catch(() => {});
        }
      })
      .catch(() => {});
  }, [customer?.source_opportunity]); // eslint-disable-line

  useEffect(() => {
    axios.get(`${API_DOMAIN}${API_ROUTES.opportunity}`, { headers, params: { page_size: 500 } })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || res.data?.data || []);
        setExpansionOpps(list.filter(o => String(o.existing_customer) === String(id)));
      })
      .catch(() => {});
  }, [id]); // eslint-disable-line

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_DOMAIN}${API_ROUTES.customer}${id}/`, { headers });
      toast?.addToast({ type: 'success', message: 'Customer deleted.' });
      navigate('/customer/list');
    } catch {
      toast?.addToast({ type: 'error', message: 'Failed to delete customer.' });
    }
  };

  if (loading) return (
    <div className="card"><div className="card-body">
      {[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: 18, width: `${60 + i * 8}%`, borderRadius: 4, marginBottom: 12 }} />)}
    </div></div>
  );

  if (!customer) return (
    <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
      Customer not found. <Link to="/customer/list" style={{ color: 'var(--color-primary)' }}>Back to list</Link>
    </div></div>
  );

  const statusColor = STATUS_COLORS[customer.status] || '#94a3b8';
  const tierCfg = TIER_CONFIG[customer.tier] || TIER_CONFIG.Standard;
  const initials = customer.name
    ? customer.name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : '?';

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
            <Link to="/customer/list" style={{ color: 'var(--color-primary)' }}>Customers</Link>
            <span>›</span>
            <span>{customer.name || `Customer #${customer.id}`}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {/* Avatar */}
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--color-primary-pale)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, flexShrink: 0 }}>
              {initials}
            </div>
            <div>
              <h1 className="page-title" style={{ margin: 0 }}>{customer.name || `Customer #${customer.id}`}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                {customer.status && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 10px', borderRadius: 99, background: statusColor + '18', border: `1px solid ${statusColor}40`, fontSize: 11, fontWeight: 600, color: statusColor }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor }} />
                    {customer.status}
                  </span>
                )}
                {customer.customer_type && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {TYPE_ICONS[customer.customer_type]} {customer.customer_type}
                  </span>
                )}
                {customer.tier && (
                  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 99, background: tierCfg.bg, color: tierCfg.fg, fontSize: 11, fontWeight: 700, border: `1px solid ${tierCfg.bar}40` }}>
                    {customer.tier}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-danger" onClick={() => setShowDelete(true)}>Delete</button>
          <button className="btn btn-secondary" onClick={() => navigate(`/customer/stage/input/${btoa(customer.id)}`)}>Edit Customer</button>
          <ConvertActionButton
            label="Start New Deal"
            url={`${API_DOMAIN}${API_ROUTES.customer}${customer.id}/start-opportunity/`}
            body={{
              opportunity_name: `${customer.name} - Expansion`,
              stage: 'Prospecting',
              value: 0,
              expected_close_date: new Date(Date.now() + 30 * 86400000).toISOString(),
              company: customer.company,
            }}
            buildNavigateTo={(opp) => `/opportunity/input/${btoa(opp.id)}`}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>

        {/* Left */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          <Section icon="📞" title="Contact Details">
            <div className="form-grid">
              <Field label="Email" value={customer.email} href={customer.email ? `mailto:${customer.email}` : null} />
              <Field label="Primary Phone" value={customer.phone} href={customer.phone ? `tel:${customer.phone}` : null} />
              <Field label="Secondary Phone" value={customer.secondary_phone} />
              <Field label="Preferred Contact" value={customer.preferred_contact} />
            </div>
          </Section>

          <Section icon="🏢" title="Business Information">
            <div className="form-grid">
              <Field label="Company / Organisation" value={customer.company} />
              <Field label="Industry" value={customer.industry} />
              <Field label="Website" value={customer.website} href={customer.website} />
              <Field label="Annual Revenue" value={fmtKES(customer.annual_revenue)} />
              <Field label="No. of Employees" value={customer.employee_count} />
              <Field label="Lead Source" value={customer.lead_source} />
            </div>
          </Section>

          <Section icon="📍" title="Location">
            <div className="form-grid">
              <Field label="Street Address" value={customer.address} wide />
              <Field label="City / Town" value={customer.city} />
              <Field label="Country" value={customer.country} />
              <Field label="Postal Code" value={customer.postal_code} />
            </div>
          </Section>

          <Section icon="🤝" title="Relationship">
            <div className="form-grid">
              <Field label="Account Manager" value={customer.account_manager} />
              <Field label="Customer Since" value={fmtDate(customer.customer_since)} />
              {customer.notes && <Field label="Internal Notes" value={customer.notes} wide />}
            </div>
          </Section>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Expansion Deals card — Journey B: upsell/cross-sell opportunities started from this customer */}
          {expansionOpps.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">Expansion Deals</span></div>
              <div className="card-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {expansionOpps.map(o => (
                    <button
                      key={o.id}
                      className="btn btn-ghost"
                      style={{ justifyContent: 'flex-start', gap: 8 }}
                      onClick={() => navigate(`/opportunity/details/${btoa(o.id)}`)}
                    >
                      💼 {o.opportunity_name} ({o.stage})
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Deal History card — Journey A/H: how this customer came to be */}
          {sourceOpp && (
            <div className="card">
              <div className="card-header"><span className="card-title">Deal History</span></div>
              <div className="card-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {sourceLead && (
                    <button
                      className="btn btn-ghost"
                      style={{ justifyContent: 'flex-start', gap: 8 }}
                      onClick={() => navigate(`/lead/details/${btoa(sourceLead.id)}`)}
                    >
                      📥 Lead: {sourceLead.title || `#${sourceLead.id}`}
                    </button>
                  )}
                  <button
                    className="btn btn-ghost"
                    style={{ justifyContent: 'flex-start', gap: 8 }}
                    onClick={() => navigate(`/opportunity/details/${btoa(sourceOpp.id)}`)}
                  >
                    💼 Opportunity: {sourceOpp.opportunity_name} ({sourceOpp.stage})
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Account card */}
          <div className="card">
            <div className="card-header"><span className="card-title">Account</span></div>
            <div className="card-body">
              {/* Tier badge */}
              {customer.tier && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: tierCfg.bg, border: `1px solid ${tierCfg.bar}40`, marginBottom: 12 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: tierCfg.bar, flexShrink: 0 }} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: tierCfg.fg }}>{customer.tier} Tier</span>
                </div>
              )}
              {[
                { label: 'Status',   value: customer.status || '—' },
                { label: 'Type',     value: `${TYPE_ICONS[customer.customer_type] || ''} ${customer.customer_type || '—'}` },
                { label: 'Source',   value: customer.lead_source || '—' },
                { label: 'Since',    value: fmtDate(customer.customer_since) },
                { label: 'Created',  value: fmtDate(customer.created_at) },
                { label: 'Updated',  value: fmtDate(customer.updated_at) },
              ].map(({ label, value: v }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
                  <span style={{ color: 'var(--text-main)', fontWeight: 500, textAlign: 'right' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div className="card">
            <div className="card-header"><span className="card-title">Related Records</span></div>
            <div className="card-body">
              {[
                { icon: '💬', label: 'Interactions',  to: `/customer/interactions/list/${btoa(customer.id)}` },
                { icon: '📝', label: 'Notes',          to: `/customer/note/list/${btoa(customer.id)}` },
                { icon: '📋', label: 'Activities',     to: `/customer/activity/list/${btoa(customer.id)}` },
                { icon: '📄', label: 'Documents',      to: `/customer/document/list/${btoa(customer.id)}` },
              ].map(({ icon, label, to }) => (
                <button key={label} className="btn btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 4, gap: 10 }} onClick={() => navigate(to)}>
                  <span>{icon}</span> {label}
                </button>
              ))}
            </div>
          </div>

          <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('/customer/list')}>
            ← Back to Customers
          </button>
        </div>
      </div>

      <ConfirmModal
        open={showDelete}
        title="Delete Customer"
        message="Are you sure you want to permanently delete this customer? This cannot be undone."
        confirmLabel="Delete"
        confirmDanger
        onConfirm={handleDelete}
        onCancel={() => setShowDelete(false)}
      />
    </div>
  );
};

export default SingleCustomerDetails;
